/**
 * ContextLinkingButton Component
 * Allows users to link sessions and patients to out-of-session Evidence queries.
 * Shows a button that opens a popover with a navigable menu for Sessions, Patients, and Files.
 *
 * Mobile: Uses a bottom sheet (Drawer) - see ContextLinkingMobileDrawer
 * Desktop: Uses Popover with Command-based searchable lists
 */

import {
  linkedContextOriginFor,
  linkedPatientContextAtom,
  linkedSessionContextAtom,
  stageLinkedPatientAtom,
  stageLinkedSessionAtom,
  type LinkedPatientContext,
  type LinkedSessionContext,
} from '@/atoms/chatAtoms';
import { IS_CHAT_ATTACHMENTS_ENABLED } from '@/app-shell/flags/evidence-flags';
import { Button } from '@heidi/design-system/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandList,
} from '@heidi/design-system/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@heidi/design-system/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { Loader } from '@heidi/snowflake-ui/loader';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { useQueryAdminSessions } from '@/hooks/query/useQuerySessionsV2';
import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type { Dispatch } from '@/store';
import { FileUploadButton } from '@heidi/design-system/file-upload';
import { useAtom, useSetAtom } from 'jotai';
import {
  AudioLines,
  Check,
  ChevronRight,
  FileText,
  Paperclip,
  Plus,
  UserRound,
} from 'lucide-react';
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { formatSessionDate } from './ContextLinkingSessionsList';
import { contextLinkingButtonMessages } from './ContextLinkingButton.messages';
import { contextLinkingSessionsListMessages } from './ContextLinkingSessionsList.messages';

import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { ContextLinkingMobileDrawer } from './ContextLinkingMobileDrawer';
import { track } from '@/lib/analytics/track';

// ============================================================================
// Types
// ============================================================================

interface ContextLinkingButtonProps {
  /** Whether to use mobile styling */
  isMobile?: boolean;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Whether context linking is locked (chat has started, can't change patient/session) */
  isContextLocked?: boolean;
  /** Handler invoked with the user-picked files; absent disables file attachment. */
  onFilesAdded?: (files: File[]) => void;
  /** When true, only show file upload (hide session/patient linking). Auto-opens file picker on click. */
  fileOnly?: boolean;
  /**
   * The chat the selection is staged for; null/undefined on a new-chat
   * surface. Recorded as the linked-context origin so the send-boundary
   * provenance gate (resolveAttachableLinkedContext) can tell a deliberate
   * pick apart from context primed by another chat's backend-sync.
   */
  chatId?: string | null;
}

// ============================================================================
// Shared submenu primitives
// ============================================================================

/** Manages open/close + search state for a nested submenu popover */
function useSubmenuState<T>(
  onSelect: (item: T) => void,
  onOpen?: () => void
) {
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [search, setSearch] = useState('');

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setSubmenuOpen(open);
      if (open) onOpen?.();
      if (!open) setSearch('');
    },
    [onOpen]
  );

  const handleSelect = useCallback(
    (item: T) => {
      setSubmenuOpen(false);
      onSelect(item);
    },
    [onSelect]
  );

  return { submenuOpen, search, setSearch, handleOpenChange, handleSelect };
}

/** Reusable nested popover shell — trigger button + searchable command list */
const SubmenuShell = ({
  icon,
  label,
  linkedName,
  isActive,
  searchPlaceholder,
  emptyMessage,
  isLoading,
  isEmpty,
  submenuOpen,
  onOpenChange,
  search,
  onSearchChange,
  children,
  testId,
}: {
  icon: ReactNode;
  label: string;
  linkedName?: string;
  isActive: boolean;
  searchPlaceholder: string;
  emptyMessage: string;
  isLoading: boolean;
  isEmpty: boolean;
  submenuOpen: boolean;
  onOpenChange: (open: boolean) => void;
  search: string;
  onSearchChange: (value: string) => void;
  children: ReactNode;
  testId?: string;
}) => (
  <Popover open={submenuOpen} onOpenChange={onOpenChange}>
    <PopoverTrigger asChild>
      <button
        type="button"
        className={cn(
          'flex w-full min-w-0 flex-col gap-0.5 overflow-hidden rounded-lg p-2 text-start transition-colors',
          'cursor-pointer hover:bg-surface-hover',
          isActive && 'bg-surface-elevated',
          submenuOpen && 'bg-surface-hover'
        )}
        aria-haspopup="true"
        aria-expanded={submenuOpen}
        aria-label={label}
        data-testid={testId}
      >
        <div className="flex w-full min-w-0 items-start gap-2">
          <div className="flex size-4 shrink-0 items-center justify-center pt-0.5">{icon}</div>
          <TypographyP2 className="min-w-0 flex-1 whitespace-normal break-normal font-normal text-text-primary">
            {label}
          </TypographyP2>
          <div className="flex shrink-0 items-start justify-center pt-0.5">
            <ChevronRight size={16} className="shrink-0 text-text-secondary rtl:-scale-x-100" />
          </div>
        </div>
        {linkedName ? (
          <div className="min-w-0 ps-6">
            <TypographyCaption className="block truncate text-text-tertiary">
              {linkedName}
            </TypographyCaption>
          </div>
        ) : null}
      </button>
    </PopoverTrigger>

    <PopoverContent
      side="right"
      align="start"
      sideOffset={4}
      collisionPadding={16}
      className="w-80 rounded-lg p-0"
    >
      <Command shouldFilter={false} className="rounded-lg border-none">
        <CommandInput
          placeholder={searchPlaceholder}
          value={search}
          onValueChange={onSearchChange}
          showClearButton
          className="h-9"
          wrapperClassName="border-b-0 pt-1"
        />
        <CommandList className="max-h-72">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader size={20} />
            </div>
          ) : isEmpty ? (
            <CommandEmpty>{emptyMessage}</CommandEmpty>
          ) : (
            <div className="p-1">{children}</div>
          )}
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
);

// ============================================================================
// Desktop Popover submenus (nested popovers opening to the right)
// ============================================================================

/** Sessions submenu — search + list in a side panel */
const SessionsSubmenu = ({
  linkedSession,
  onSelect,
  onClear,
  isContextLocked,
  onOpen,
}: {
  linkedSession: LinkedSessionContext | null;
  onSelect: (session: LinkedSessionContext) => void;
  onClear: () => void;
  isContextLocked: boolean;
  onOpen?: () => void;
}) => {
  const intl = useIntl();
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useQueryAdminSessions();

  const { submenuOpen, search, setSearch, handleOpenChange, handleSelect } =
    useSubmenuState(onSelect, onOpen);

  const allSessions = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.sessions);
  }, [data?.pages]);

  const filteredSessions = useMemo(() => {
    if (!search.trim()) return allSessions;
    const q = search.toLowerCase();
    return allSessions.filter(
      (s) =>
        s.session_name?.toLowerCase().includes(q) ||
        s.patient?.name?.toLowerCase().includes(q)
    );
  }, [allSessions, search]);

  return (
    <SubmenuShell
      icon={<AudioLines size={16} className="text-text-primary" />}
      label={intl.formatMessage(contextLinkingButtonMessages.linkAConsultation)}
      linkedName={linkedSession?.name}
      isActive={!!linkedSession}
      searchPlaceholder={intl.formatMessage(
        contextLinkingButtonMessages.findAConsultation
      )}
      emptyMessage={intl.formatMessage(
        contextLinkingSessionsListMessages.noSessionsFound
      )}
      isLoading={isLoading}
      isEmpty={filteredSessions.length === 0}
      submenuOpen={submenuOpen}
      onOpenChange={handleOpenChange}
      search={search}
      onSearchChange={setSearch}
      testId="context-link-consultation-trigger"
    >
      {filteredSessions.map((session) => {
        const isLinked = linkedSession?.id === session.session_id;
        const sessionName =
          session.session_name ||
          intl.formatMessage(contextLinkingSessionsListMessages.untitledSession);

        const handleClick = isContextLocked
          ? undefined
          : () => {
              if (isLinked) {
                onClear();
              } else {
                handleSelect({
                  id: session.session_id,
                  name: sessionName,
                  patientName: session.patient?.name ?? undefined,
                  createdAt: session.created_time_local ?? undefined,
                });
              }
            };

        return (
          <button
            key={session.session_id}
            type="button"
            disabled={isContextLocked}
            onClick={handleClick}
            data-testid="context-linking-session-item"
            data-session-id={session.session_id}
            className={cn(
              'flex w-full flex-col items-start gap-0.5 rounded-md p-2 text-start',
              'hover:bg-surface-hover',
              isContextLocked && 'cursor-not-allowed opacity-50'
            )}
          >
            <div className="flex w-full items-center gap-2">
              <FileText size={14} className="shrink-0 text-text-secondary" />
              <TypographyP2 className="flex-1 truncate">
                {sessionName}
              </TypographyP2>
              {isLinked && (
                <Check size={14} className="shrink-0 text-text-primary" />
              )}
            </div>
            <div className="flex w-full items-center gap-3 ps-5">
              {session.patient?.name && (
                <TypographyCaption className="flex items-center gap-1 text-text-tertiary">
                  <UserRound size={10} />
                  {session.patient.name}
                </TypographyCaption>
              )}
              {session.created_time_local && (
                <TypographyCaption className="text-text-tertiary">
                  {formatSessionDate(session.created_time_local, intl)}
                </TypographyCaption>
              )}
            </div>
          </button>
        );
      })}

      {/* Load more (only when not filtering) */}
      {!search.trim() && hasNextPage && (
        <button
          type="button"
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
          className="flex w-full items-center justify-center rounded-md p-2 text-sm text-text-secondary hover:bg-surface-hover"
        >
          {isFetchingNextPage ? (
            <Loader size={14} />
          ) : (
            intl.formatMessage(contextLinkingSessionsListMessages.loadMore)
          )}
        </button>
      )}
    </SubmenuShell>
  );
};

/** Patients submenu — search + list in a side panel */
const PatientsSubmenu = ({
  linkedPatient,
  onSelect,
  onClear,
  isContextLocked,
  onOpen,
}: {
  linkedPatient: LinkedPatientContext | null;
  onSelect: (patient: LinkedPatientContext) => void;
  onClear: () => void;
  isContextLocked: boolean;
  onOpen?: () => void;
}) => {
  const intl = useIntl();
  const { data: patients, isLoading } = useQueryPatientProfiles();

  const { submenuOpen, search, setSearch, handleOpenChange, handleSelect } =
    useSubmenuState(onSelect, onOpen);

  const filteredPatients = useMemo(() => {
    if (!patients) return [];
    const withId = patients.filter((p) => p.id);
    if (!search.trim()) return withId;
    const q = search.toLowerCase();
    return withId.filter(
      (p) =>
        p.first_name?.toLowerCase().includes(q) ||
        p.last_name?.toLowerCase().includes(q) ||
        p.demographic_string?.toLowerCase().includes(q)
    );
  }, [patients, search]);

  return (
    <SubmenuShell
      icon={<UserRound size={16} className="text-text-primary" />}
      label={intl.formatMessage(contextLinkingButtonMessages.linkAPatient)}
      linkedName={linkedPatient?.name}
      isActive={!!linkedPatient}
      searchPlaceholder={intl.formatMessage(contextLinkingButtonMessages.findAPatient)}
      emptyMessage={intl.formatMessage(contextLinkingButtonMessages.noPatientsFound)}
      isLoading={isLoading}
      isEmpty={filteredPatients.length === 0}
      submenuOpen={submenuOpen}
      onOpenChange={handleOpenChange}
      search={search}
      onSearchChange={setSearch}
      testId="context-link-patient-trigger"
    >
      {filteredPatients.map((patient) => {
        if (!patient.id) return null;
        const patientId = patient.id;
        const isLinked = linkedPatient?.id === patientId;
        const fullName = [patient.first_name, patient.last_name]
          .filter(Boolean)
          .join(' ');
        const displayName =
          fullName ||
          patient.demographic_string ||
          intl.formatMessage(contextLinkingButtonMessages.unknownPatient);
        const showDemographic = fullName && patient.demographic_string;

        const handleClick = isContextLocked
          ? undefined
          : () => {
              if (isLinked) {
                onClear();
              } else {
                handleSelect({
                  id: patientId,
                  name: displayName,
                  demographicString: showDemographic
                    ? patient.demographic_string
                    : undefined,
                });
              }
            };

        return (
          <button
            key={patientId}
            type="button"
            disabled={isContextLocked}
            onClick={handleClick}
            data-testid="context-linking-patient-item"
            data-patient-id={patientId}
            className={cn(
              'flex w-full flex-col items-start gap-0.5 rounded-md p-2 text-start',
              'hover:bg-surface-hover',
              isContextLocked && 'cursor-not-allowed opacity-50'
            )}
          >
            <div className="flex w-full items-center gap-2">
              <UserRound size={14} className="shrink-0 text-text-secondary" />
              <TypographyP2 className="flex-1 truncate">
                {displayName}
              </TypographyP2>
              {isLinked && (
                <Check size={14} className="shrink-0 text-text-primary" />
              )}
            </div>
            {showDemographic && (
              <TypographyCaption className="ps-5 text-text-tertiary">
                {patient.demographic_string}
              </TypographyCaption>
            )}
          </button>
        );
      })}
    </SubmenuShell>
  );
};

// ============================================================================
// Main component
// ============================================================================

export const ContextLinkingButton = ({
  isMobile = false,
  disabled = false,
  isContextLocked = false,
  onFilesAdded,
  fileOnly = false,
  chatId,
}: ContextLinkingButtonProps) => {
  const dispatch = useDispatch<Dispatch>();
  const { isInSessionEvidenceShown, isPending: isAccessPending } = useEvidenceAccess();
  const { data: userTier } = useQueryUserBillingInfo((info) => info?.new_product_tier);
  const canLinkContext =
    isInSessionEvidenceShown && userTier !== SubscriptionTier.FREE;
  const [linkedSession, setLinkedSession] = useAtom(linkedSessionContextAtom);
  const [linkedPatient, setLinkedPatient] = useAtom(linkedPatientContextAtom);
  const stageLinkedSession = useSetAtom(stageLinkedSessionAtom);
  const stageLinkedPatient = useSetAtom(stageLinkedPatientAtom);
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const justClosedRef = useRef(false);

  // Track button clicks
  const trackAddSessionClicked = useCallback(() => {
    track({ options: { amplitude: false },
      event_name: TrackingEventName.ChatContextAddSessionClicked,
      props: { is_mobile: isMobile },
    });
  }, [dispatch, isMobile]);

  const trackAttachPatientClicked = useCallback(() => {
    track({ options: { amplitude: false },
      event_name: TrackingEventName.ChatContextAttachPatientClicked,
      props: { is_mobile: isMobile },
    });
  }, [dispatch, isMobile]);

  const trackAddFileClicked = useCallback(() => {
    track({ options: { amplitude: false },
      event_name: TrackingEventName.ChatContextAddFileClicked,
      props: { is_mobile: isMobile },
    });
  }, [dispatch, isMobile]);

  const handleSelectSession = useCallback(
    (session: LinkedSessionContext) => {
      // Stage the session (single-slot: clears any linked patient).
      stageLinkedSession({ session, origin: linkedContextOriginFor(chatId) });
      setIsOpen(false);
    },
    [stageLinkedSession, chatId]
  );

  const handleSelectPatient = useCallback(
    (patient: LinkedPatientContext) => {
      // Stage the patient (single-slot: clears any linked session).
      stageLinkedPatient({ patient, origin: linkedContextOriginFor(chatId) });
      setIsOpen(false);
    },
    [stageLinkedPatient, chatId]
  );

  const handleClearSession = useCallback(() => {
    setLinkedSession(null);
  }, [setLinkedSession]);

  const handleClearPatient = useCallback(() => {
    setLinkedPatient(null);
  }, [setLinkedPatient]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (open) {
      setTooltipOpen(false);
    } else {
      justClosedRef.current = true;
      setTimeout(() => {
        justClosedRef.current = false;
      }, 150);
    }
  }, []);

  const handleTooltipOpenChange = useCallback(
    (open: boolean) => {
      if (open && (isOpen || justClosedRef.current)) return;
      setTooltipOpen(open);
    },
    [isOpen]
  );

  const hasFileAttachment = IS_CHAT_ATTACHMENTS_ENABLED() && !!onFilesAdded;
  const hasMenuContent = canLinkContext || hasFileAttachment;

  if (!hasMenuContent && !(fileOnly && onFilesAdded) && !isAccessPending) {
    return null;
  }

  // File-only mode: auto-open file picker on click (no dropdown/drawer)
  if (fileOnly && onFilesAdded) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <FileUploadButton
            asChild
            multiple
            onFilesAccepted={(files) => {
              trackAddFileClicked();
              onFilesAdded(files);
            }}
          >
            <Button
              type="button"
              variant="ghost"
              size={isMobile ? 'lg' : 'sm'}
              disabled={disabled}
              data-testid="context-linking-button"
              iconOnly
              disableAutomaticSvgSizing
            >
              <Plus className="size-4 shrink-0 text-text-secondary" />
            </Button>
          </FileUploadButton>
        </TooltipTrigger>
        <TooltipContent side="top">
          <FormattedMessage {...contextLinkingButtonMessages.addFile} />
        </TooltipContent>
      </Tooltip>
    );
  }

  // Mobile: Use bottom sheet drawer
  if (isMobile) {
    return (
      <ContextLinkingMobileDrawer
        disabled={disabled}
        isContextLocked={isContextLocked}
        isInSessionEvidenceShown={canLinkContext}
        onFilesAdded={onFilesAdded}
        linkedSession={linkedSession}
        linkedPatient={linkedPatient}
        onSelectSession={handleSelectSession}
        onSelectPatient={handleSelectPatient}
        onClearSession={handleClearSession}
        onClearPatient={handleClearPatient}
      />
    );
  }

  // Desktop: Popover with navigable Command menu
  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <Tooltip open={tooltipOpen} onOpenChange={handleTooltipOpenChange}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              data-testid="context-linking-button"
              iconOnly
              disableAutomaticSvgSizing
            >
              <Plus className="size-4 shrink-0 text-text-secondary" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">
          <FormattedMessage
            {...contextLinkingButtonMessages.addConsultPatientHistoryOrFiles}
          />
        </TooltipContent>
      </Tooltip>

      <PopoverContent
        align="start"
        className="w-56 min-w-0 p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex min-w-0 flex-col p-1">
          {canLinkContext && (
            <>
              <SessionsSubmenu
                linkedSession={linkedSession}
                onSelect={handleSelectSession}
                onClear={handleClearSession}
                isContextLocked={isContextLocked}
                onOpen={trackAddSessionClicked}
              />

              <PatientsSubmenu
                linkedPatient={linkedPatient}
                onSelect={handleSelectPatient}
                onClear={handleClearPatient}
                isContextLocked={isContextLocked}
                onOpen={trackAttachPatientClicked}
              />
            </>
          )}
          {IS_CHAT_ATTACHMENTS_ENABLED() && onFilesAdded && (
            <FileUploadButton
              asChild
              multiple
              onFilesAccepted={(files) => {
                trackAddFileClicked();
                onFilesAdded(files);
                setIsOpen(false);
              }}
              className="w-full"
            >
              <button
                type="button"
                className="flex w-full cursor-pointer items-center gap-2 rounded-lg p-2 text-start transition-colors hover:bg-surface-hover"
              >
                <div className="flex size-4 items-center justify-center">
                  <Paperclip size={16} className="text-text-primary" />
                </div>
                <TypographyP2 className="font-normal text-text-primary">
                  <FormattedMessage {...contextLinkingButtonMessages.addFile} />
                </TypographyP2>
              </button>
            </FileUploadButton>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
